/*
 * File: Topbar.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 7, 2022 at 1:51 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 4, 2025 at 5:34 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useState, useEffect, useRef, useContext
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useDebouncedCallback, useWindowResize } from 'beautiful-react-hooks';

// Context
import ActionContext from '../../../context/SharedActionContext';
import AnimationContextBuilder from '../../../context/AnimationContext';

// Utilities
import { leftCenterPosition, topCenterPosition } from '../../../utilities/position';

// Components
import { Button } from '../../Button';
import { Typography } from '../../Typography';
import { Logo } from '../../Logo';
import { NavigationTab } from '../../NavigationTab';
import { Emoji } from '../../Emoji';
import { ToolTip } from '../../ToolTip';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../constants/base.constants';
import { AppNavTopbarHeight, AppNavTopbarMobileSegmentHeight } from '../../../styles/constants';

// Styles
import * as S from './Topbar.styles';


/**
 * Component
 */

export const Topbar = ({
	toggleNavigation, isFixed, hideMobileNavigation
}) => {

	// Get context from navigation parent
	const {
		dispatch: actionDispatch
	} = useContext(ActionContext);

	// Get context from navigation parent
	const {
		dispatch: contextDispatch,
	} = useContext(AnimationContextBuilder);

	// Set state
	const [isScrolled, setIsScrolled] = useState(false);

	// Create references for components
	const isMounted = useRef(true);
	const mobileNavigationToggleRef = useRef();

	// Get current user from hook
	const currentUser = useSelector((state) => state.user.value);

	// Use hooks
	const navigate = useNavigate();

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Handle debounce function for updating context
	const debounceUpdateContext = useDebouncedCallback(() => {
		if (isMounted.current) {

			// Get ref parameters
			const navRect = mobileNavigationToggleRef?.current?.getBoundingClientRect();

			// Set new position values
			contextDispatch({
				type: 'SET_MOBILE_REACTION_METRIC_POSITION',
				payload: {
					left: leftCenterPosition(navRect),
					top: topCenterPosition(navRect)
				}
			});
		}
	}, [], 200);

	// Handle actions on window resize
	useWindowResize(() => {

		// Update context with debounce
		debounceUpdateContext();
	});

	// Handle actions on component load
	useEffect(() => {

		// Initialize temporary state
		isMounted.current = true;

		// Update context with debounce
		debounceUpdateContext();

		// Attach scroll listener
		if (isMounted.current) {
			document.addEventListener('scroll', () => {
				if (isMounted.current) {

					// Get current scroll status
					const st = document.scrollingElement.scrollTop;

					// Update context with debounce
					debounceUpdateContext();

					// Update scrolled status
					setIsScrolled(st >= 20);
				}
			}, true);
		}

		// Handle actions on dismount
		return () => { isMounted.current = false; };

	}, []);

	// Render component
	return (
		<S.Topbar
			$isFixed={isFixed}
			$isScrolled={isScrolled}
			className="animate"
		>
			<S.TopbarPadding left right>
				<S.TopbarPrimarySegment>

					{/* Left Components */}
					<S.TopbarWrapper>

						{/* Mobile navigation toggle */}
						{!hideMobileNavigation && (
							<S.NavigationToggle
								icon={['far', 'bars']}
								size={1.25}
								$isScrolled={isScrolled}
								$isFixed={isFixed}
								onClick={toggleNavigation}
								className="condensedNavigation"
								ref={mobileNavigationToggleRef}
							/>
						)}

						{/* Logo */}
						<Logo className="logoImage" link="/" />

					</S.TopbarWrapper>

					{/* Center Components */}
					<S.TopbarWrapper $fullWidth>

						{/* Tab Navigation */}
						<S.NavigationTabContainer className="expandedNavigation" $height={AppNavTopbarHeight}>
							<NavigationTab
								to="/"
								className="paddingLeft"
								icon={['fal', 'home-lg-alt']}
								activeIcon={['fas', 'home-lg-alt']}
								label={t('topBar.navigationTabCommunity')}
								title={t('topBar.navigationTabCommunity')}
							/>
							<NavigationTab
								to="/learn"
								icon={['fal', 'message-question']}
								activeIcon={['fas', 'message-question']}
								label={t('topBar.navigationTabQA')}
								title={t('topBar.navigationTabQA')}
							/>
							<NavigationTab
								to="/resources"
								icon={['fal', 'compass']}
								activeIcon={['fas', 'compass']}
								label={t('topBar.navigationTabSupport')}
								title={t('topBar.navigationTabSupport')}
							/>
						</S.NavigationTabContainer>

					</S.TopbarWrapper>

					{/* Right Components */}
					<S.TopbarWrapper>

						{/* Impersonation Indicator */}
						{currentUser.isImpersonation && (
							<>
								{/* Tooltip */}
								<ToolTip id="impersonation-indicator" weight="medium" place="bottom" />

								{/* Indicator */}
								<S.ImpersonationIndicator
									data-tip={t('topBar.impersonationTooltip')}
									data-for="impersonation-indicator"
									onClick={() => {
										window.location = '/logout';
									}}
								>
									<Emoji symbol="🤖" label="robot" size={1.5} />
								</S.ImpersonationIndicator>
							</>
						)}

						{/* Button Container */}
						<S.ButtonContainer>

							{/* Share Story */}
							<Button
								variation="secondary"
								onClick={() => {
									navigate('/share');
								}}
							>
								<Typography variation="button-medium" weight="semibold" className="condensedNavigation">
									<Trans t={t} i18nKey="actions.share">
										Share
									</Trans>
								</Typography>
								<Typography variation="button-medium" weight="semibold" className="expandedNavigation">
									<Trans t={t} i18nKey="actions.shareStory">
										Share story
									</Trans>
								</Typography>
							</Button>

							{/* Post a message */}
							<Button
								onClick={() => {
									actionDispatch({
										type: 'OPEN_MESSAGE_CREATOR_MODAL',
										payload: {}
									});
								}}
							>
								<Typography variation="button-medium" weight="semibold" className="condensedNavigation">
									<Trans t={t} i18nKey="actions.post">
										Post
									</Trans>
								</Typography>
								<Typography variation="button-medium" weight="semibold" className="expandedNavigation">
									<Trans t={t} i18nKey="actions.postMessage">
										Post a message
									</Trans>
								</Typography>
							</Button>

						</S.ButtonContainer>

					</S.TopbarWrapper>
				</S.TopbarPrimarySegment>
				<S.TopbarMobileSegment className="condensedNavigation">

					{/* Mobile Navigation Tab Container */}
					<S.NavigationTabContainer $height={AppNavTopbarMobileSegmentHeight}>
						<NavigationTab
							to="/"
							className="paddingLeft"
							icon={['fal', 'home-lg-alt']}
							activeIcon={['fas', 'home-lg-alt']}
							label={t('topBar.navigationTabCommunity')}
							mobileLabel={t('topBar.navigationTabCommunityMobile')}
							title={t('topBar.navigationTabCommunity')}
							wrapperHeight={AppNavTopbarMobileSegmentHeight}
						/>
						<NavigationTab
							to="/learn"
							icon={['fal', 'message-question']}
							activeIcon={['fas', 'message-question']}
							label={t('topBar.navigationTabQA')}
							mobileLabel={t('topBar.navigationTabQAMobile')}
							title={t('topBar.navigationTabQA')}
							wrapperHeight={AppNavTopbarMobileSegmentHeight}
						/>
						<NavigationTab
							to="/resources"
							icon={['fal', 'compass']}
							activeIcon={['fas', 'compass']}
							label={t('topBar.navigationTabSupport')}
							mobileLabel={t('topBar.navigationTabSupportMobile')}
							title={t('topBar.navigationTabSupport')}
							wrapperHeight={AppNavTopbarMobileSegmentHeight}
						/>
					</S.NavigationTabContainer>

				</S.TopbarMobileSegment>
			</S.TopbarPadding>
		</S.Topbar>
	);
};


/**
 * Configuration
 */

Topbar.displayName = 'Topbar';
Topbar.propTypes = {
	toggleNavigation: PropTypes.func,
	isFixed: PropTypes.bool,
	hideMobileNavigation: PropTypes.bool
};
Topbar.defaultProps = {
	toggleNavigation: null,
	isFixed: true,
	hideMobileNavigation: false
};
